import { getRouteApi, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/site/AuthShell";
import { FieldError, fieldErrors, Notice, ProblemAlert } from "@/components/site/Feedback";
import { api } from "@/lib/api";

const routeApi = getRouteApi("/identity/account/ResetPassword");

/** Email link: /identity/account/ResetPassword?code=...&email=... */
export function ResetPasswordPage() {
  usePageTitle("Choose a new password");
  const { code, email: linkEmail } = routeApi.useSearch();
  const [email, setEmail] = useState(linkEmail ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const mismatch = confirmPassword && password !== confirmPassword ? "The passwords do not match." : null;

  const reset = useMutation({
    mutationFn: () => api.auth.resetPassword({ email: email.trim(), code: code!, password, confirmPassword }),
  });

  return (
    <AuthShell eyebrow="Members' portal" title="Choose a new password">
      {!code ? (
        <ProblemAlert error={new Error("This link is invalid or incomplete. Request a new password reset email.")} />
      ) : reset.isSuccess ? (
        <Notice tone="success" title="Your password was changed">
          Sign in with your new password.
        </Notice>
      ) : (
        <form
          className="space-y-5"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!mismatch) reset.mutate();
          }}
        >
          <ProblemAlert error={reset.error} />
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" required value={email} readOnly={!!linkEmail} onChange={(e) => setEmail(e.target.value)} />
            <FieldError messages={fieldErrors(reset.error, "Email")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">New password</Label>
            <Input id="password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            <FieldError messages={fieldErrors(reset.error, "Password")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input id="confirm" type="password" autoComplete="new-password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            <FieldError messages={[mismatch, ...(fieldErrors(reset.error, "ConfirmPassword") ?? [])]} />
          </div>
          <Button type="submit" variant="gold" size="lg" className="w-full" disabled={reset.isPending || !email || !password || !confirmPassword}>
            {reset.isPending && <Loader2 className="animate-spin" />}
            Set new password
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm text-muted-foreground">
        {code && !reset.isSuccess ? (
          <Link to="/forgot-password" className="underline underline-offset-4">
            Request a new link
          </Link>
        ) : null}
      </p>
      {(reset.isSuccess || !code) && (
        <Button asChild variant="gold" size="lg" className="w-full">
          <Link to={reset.isSuccess ? "/login" : "/forgot-password"}>{reset.isSuccess ? "Sign in" : "Request a new link"}</Link>
        </Button>
      )}
    </AuthShell>
  );
}
